import { getUrl } from "@insite/client-framework/ServerSideRendering";
import * as gvDesign from "@tSurya/theme/design.globalviews";
import * as mgbwDesign from "@tSurya/theme/design.mgbw";
import * as suryaDesign from "@tSurya/theme/design.surya";

// theme config
export const THEME_CONFIG = {
    globalviews: {
        // TODO: update hardcoded domains
        domains: [
            "global-views.surya.local.com",
            "global-views.suryauk.local.com",
            "global-views.com",
            "global-views-ezugix04sandboxaz.commerce.insitesandbox.com",
            "global-views-ezugix05sandboxaz.commerce.insitesandbox.com",
            "global-views-surya.commerce.insitesandbox.com",
            "global-views.surya.com",
            "globalviews.surya.com",
            "preview-global-views.surya.com",
        ],
        design: gvDesign,
        name: "Global Views",
    },
    mgbw: {
        // TODO: update hardcoded domains
        domains: [
            'mgbw.surya.local.com',
            'mgbw.com',
            'mgbw-ezugix04sandboxaz.commerce.insitesandbox.com',
            'mgbw.surya.com',
            'mgbw-surya.commerce.insitesandbox.com'
        ],
        design: mgbwDesign,
        name: 'MGBW'
    },
    surya: {
        // TODO: update hardcoded domains
        domains: [
            "surya.local.com",
            "surya.com",
            "uk.surya.com",
            "suryauk.local.com",
            "ezugix04sandboxaz.commerce.insitesandbox.com",
            "surya.commerce.insitesandbox.com",
            "suryauk-ezugix05sandboxaz.local.com",
            "ezugix05sandboxaz.commerce.insitesandbox.com",
            "suryauk-ezugix05sandboxaz.commerce.insitesandbox.com",
            "suryauk-surya.commerce.insitesandbox.com",
        ],
        design: suryaDesign,
        name: "Surya",
    },
};

export type ThemeKey = keyof typeof THEME_CONFIG;

export function getThemeFromDomain(hostname: string): ThemeKey {
    const normalizedHost = hostname.toLowerCase().split(':')[0];

    for (const themeKey of Object.keys(THEME_CONFIG) as ThemeKey[]) {
        const config = THEME_CONFIG[themeKey];
        for (const domain of config.domains) {
            if (normalizedHost === domain || normalizedHost.endsWith(`.${domain}`)) {
                return themeKey;
            }
        }
    }

    return 'surya';
}

/**
 * Returns the correct design module for the current request context.
 * - Client: resolves once based on window.location.hostname (cached).
 * - Server: resolves per-request based on getUrl() from ServerSideRendering.
 *
 * Note: MyAccount Surya overrides are NOT applied here because the design
 * proxy values are snapshotted by lodash.merge into the styled-components
 * theme at app init - pathname-dependent resolution would leak Surya values
 * into non-MyAccount pages for the entire SPA session. Use
 * MyAccountThemeWrapper and ThemeFontLoader for MyAccount-specific styling.
 */
let _clientDesignCache: typeof suryaDesign | typeof gvDesign | typeof mgbwDesign | undefined;
let _clientThemeKeyCache: ThemeKey | undefined;

type AnyDesignModule = typeof suryaDesign | typeof gvDesign | typeof mgbwDesign;

const MY_ACCOUNT_PATH_RE = /^\/myaccount(\/|$)/i;
const MY_ACCOUNT_SURYA_THEME_OVERRIDE_KEYS: ThemeKey[] = ["mgbw", "globalviews"];

function isMyAccountPath(pathname: string | undefined | null): boolean {
    return !!pathname && MY_ACCOUNT_PATH_RE.test(pathname);
}

export function shouldUseSuryaForMyAccountTheme(
    themeKey: ThemeKey,
    pathname: string | undefined | null,
): boolean {
    return MY_ACCOUNT_SURYA_THEME_OVERRIDE_KEYS.includes(themeKey) && isMyAccountPath(pathname);
}

export function getCurrentDesign(): AnyDesignModule {
    if (typeof window !== "undefined") {
        if (!_clientThemeKeyCache) {
            _clientThemeKeyCache = getThemeFromDomain(window.location.hostname);
            _clientDesignCache = THEME_CONFIG[_clientThemeKeyCache].design;
        }
        // NOTE: Do NOT check pathname here. The design proxy feeds into the
        // styled-components theme via lodash.merge in ClientApp.tsx, which
        // snapshots all values ONCE at app init. If the app initializes on a
        // /MyAccount/ page the Surya values would leak into every subsequent
        // page for the entire SPA session. MyAccount Surya overrides are
        // handled at the component level by MyAccountThemeWrapper instead.
        return _clientDesignCache!;
    }

    // Server: resolve per-request (hostname only, not pathname)
    try {
        var url = getUrl();
        if (url) {
            var serverThemeKey = getThemeFromDomain(url.hostname);
            return THEME_CONFIG[serverThemeKey].design;
        }
    } catch (_e) {
        // getUrl() can throw when called outside a request context (module load time).
        // Fall through to default.
    }
    return suryaDesign;
}

/**
 * Creates a deep Proxy that lazily delegates property access to the current design's
 * export for the given key (e.g. "COLORS", "TYPOGRAPHY"). This ensures that every
 * property read returns the correct brand value for the current request, even during SSR.
 */
function createDesignProxy<K extends keyof AnyDesignModule>(propName: K): AnyDesignModule[K] {
    var handler: ProxyHandler<any> = {
        get: function(_target: any, prop: any) {
            var source = getCurrentDesign()[propName] as any;
            var val = source[prop];
            if (typeof val === "object" && val !== null && !(val instanceof Function)) {
                // Return a nested proxy so deep reads (e.g. COLORS.primary.base) also resolve dynamically
                return new Proxy(val, {
                    get: function(nestedTarget: any, nestedProp: any) {
                        // Re-resolve from current design to handle per-request context.
                        // Fall back to the originally captured target when the current
                        // design no longer exposes this nested object (e.g. brand-specific
                        // keys), so lodash.merge / isPlainObject probing never reads off undefined.
                        var freshSource = (getCurrentDesign()[propName] as any)[prop];
                        return (freshSource != null ? freshSource : nestedTarget)[nestedProp];
                    },
                    ownKeys: function(nestedTarget: any) {
                        var freshSource = (getCurrentDesign()[propName] as any)[prop];
                        return Reflect.ownKeys(freshSource != null ? freshSource : nestedTarget);
                    },
                    getOwnPropertyDescriptor: function(nestedTarget: any, p: any) {
                        var freshSource = (getCurrentDesign()[propName] as any)[prop];
                        var resolved = freshSource != null ? freshSource : nestedTarget;
                        if (p in resolved) {
                            return { configurable: true, enumerable: true, writable: true, value: resolved[p] };
                        }
                        return undefined;
                    },
                    has: function(nestedTarget: any, p: any) {
                        var freshSource = (getCurrentDesign()[propName] as any)[prop];
                        return p in (freshSource != null ? freshSource : nestedTarget);
                    }
                });
            }
            return val;
        },
        ownKeys: function() {
            return Reflect.ownKeys(getCurrentDesign()[propName] as any);
        },
        getOwnPropertyDescriptor: function(_target: any, prop: any) {
            var source = getCurrentDesign()[propName] as any;
            if (prop in source) {
                return { configurable: true, enumerable: true, writable: true, value: source[prop] };
            }
            return undefined;
        },
        has: function(_target: any, prop: any) {
            return prop in (getCurrentDesign()[propName] as any);
        }
    };

    return new Proxy({} as any, handler);
}

export const COLORS = createDesignProxy("COLORS");
export const TYPOGRAPHY = createDesignProxy("TYPOGRAPHY");
export const SPACING = createDesignProxy("SPACING");
export const PRODUCT = createDesignProxy("PRODUCT");
export const COMMON = createDesignProxy("COMMON");
export const MENU = createDesignProxy("MENU");
export const FORM = createDesignProxy("FORM");
export const ELEMENTS = createDesignProxy("ELEMENTS");
export const WIDGETS = createDesignProxy("WIDGETS");
export const Z_INDEX = createDesignProxy("Z_INDEX");
export const COMPONENTS = createDesignProxy("COMPONENTS");

